// @ts-nocheck
import React, { useEffect, useState, useRef } from "react";
import { GumletPlayer } from "@gumlet/react-embed-player";
import {
    useLazyGetLivestreamPlayerDetailsQuery,
    useLazyGetModulePlayerDetailsQuery,
    useLazyGetNextEpisodeQuery,
    usePostAcademyProgressMutation,
} from "../../../store/api/academyApi";
import {
    AcademyVideoIcon,
    ProgressCircularBar,
} from "../../../icons/academy-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { EpisodeData } from "./module";
import { getLocalStorage } from "../../../helpers/storage";
import { trackEvent } from "../../../analytics";

const VideoPlayer: React.FC<{
    episodeData?: EpisodeData | null;
    episode_id: string;
    isLivestream?: boolean;
}> = ({ episodeData, episode_id, isLivestream = false }) => {
    const [videoData, setVideoData] = useState<any>(null);
    const [getModulePlayerDetails] = useLazyGetModulePlayerDetailsQuery();
    const [triggerAcademyProgress] = usePostAcademyProgressMutation();
    const [getLivestreamPlayerDetails] =
        useLazyGetLivestreamPlayerDetailsQuery();
    const playerRef = useRef<any>(null);
    const [token, setToken] = useState<string | null>(null);
    const [expires, setExpires] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const user = JSON.parse(getLocalStorage("userData") || "{}");
    const [currentWatchTime, setCurrentWatchTime] = useState(0);
    const [totalDuration, setTotalDuration] = useState(0);
    const [episodeDetail, setEpisodeDetail] = useState<any>(null);
    const latestValues = useRef({ time: 0, duration: 0 });
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [seekDetected, setSeekDetected] = useState(false);
    const lastTimeRef = useRef(0);
    const [wasPaused, setWasPaused] = useState(true);
    const [speedChangeProcessing, setSpeedChangeProcessing] = useState(false);
    const [playEventProcessing, setPlayEventProcessing] = useState(false);
    const lastSpeedRef = useRef(1);

    const onError = (error: Error) => {
        console.error(error);
    };

    const getVideoData = async () => {
        try {
            let response;
            if (isLivestream) {
                response = await getLivestreamPlayerDetails({
                    livestreamId: episodeData?.episode_id,
                })?.unwrap();
            } else {
                response = await getModulePlayerDetails({
                    episodeId: episode_id,
                })?.unwrap();
            }
            setVideoData(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (videoData) {
            setToken(
                videoData?.widevine_signed_url
                    .split("?")[1]
                    .split("&")[1]
                    .split("=")[1]
            );
            setExpires(
                videoData?.widevine_signed_url
                    .split("?")[1]
                    .split("&")[0]
                    .split("=")[1]
            );
        }
    }, [videoData]);

    const [getNextEpisode] = useLazyGetNextEpisodeQuery();
    const [nextEpisodeData, setNextEpisodeData] = useState<any>(null);

    const getNextEpisodeData = async () => {
        try {
            const response = await getNextEpisode({
                episodeId: episode_id,
            })?.unwrap();
            setNextEpisodeData(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        Promise.all([getVideoData(), getNextEpisodeData()]);
    }, []);

    const sendVideoAnalytics = async () => {
        try {
            await triggerAcademyProgress({
                body: {
                    episode_id,
                    progress: Math.round(
                        await playerRef?.current?.getCurrentTime()
                    ),
                },
            })?.unwrap();
        } catch (error) {
            console.log(error);
        }
    };

    const handleAnalytics = async () => {
        try {
            const time = Math.round(await playerRef?.current?.getCurrentTime());
            const duration = Math.round(
                await playerRef?.current?.getDuration()
            );

            setCurrentWatchTime(time);
            setTotalDuration(duration);
            latestValues.current = { time, duration };

            if (time % 10 === 0 && time >= 10) {
                sendVideoAnalytics();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const [showNextButton, setShowNextButton] = useState(false);
    const handleVideoEnd = () => {
        if (!nextEpisodeData?.next_episode_id) return;
        setShowNextButton(true);
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
        loaderAnimation();
    };

    const trackPlayerAction = (action, extraProperties = {}) => {
        trackEvent("course_module_episode_player_action_registered", {
            episode_id,
            module_id: episodeDetail?.module_id,
            course_id: episodeDetail?.course_id,
            action_type: action,
            episode_title: episodeData?.title,
            course_title: episodeDetail?.course_title,
            module_title: episodeDetail?.module_title,
            ...extraProperties
        });
    };

    const handlePause = async () => {
        await sendVideoAnalytics();
        trackPlayerAction("pause");
        setWasPaused(true);
    };

    const handlePlay = () => {
        // Skip if we're already processing a play event
        if (playEventProcessing) return;

        // Only track play event if the player was previously paused
        if (wasPaused) {
            setPlayEventProcessing(true);
            trackPlayerAction("play");
            setWasPaused(false);

            // Reset play event processing flag after a delay
            setTimeout(() => {
                setPlayEventProcessing(false);
            }, 1000); // 1 second debounce
        }
    };

    const handleSeek = async (data) => {
        try {
            // Don't process if we already detected a seek recently
            if (seekDetected) return;

            // Get current time from the event or from the player
            const currentTime = Math.round(
                data?.seconds ||
                data?.currentTime ||
                await playerRef?.current?.getCurrentTime()
            );

            // Get previous time, with fallbacks
            const prevTime = data?.previousTime || lastTimeRef.current || latestValues.current.time;

            // Calculate the seek difference
            const seekDifference = currentTime - prevTime;

            // Only process significant seeks (more than 3 seconds difference)
            if (Math.abs(seekDifference) > 3) {
                const action = seekDifference > 0 ? "forward" : "backward";

                // Set seek detection flag to prevent duplicate events
                setSeekDetected(true);
                setTimeout(() => setSeekDetected(false), 500);

                // Track the seek event
                trackPlayerAction(action);

                // Update latest values after seek
                latestValues.current.time = currentTime;
                lastTimeRef.current = currentTime;
            }
        } catch (error) {
            console.error("Error handling seek:", error);
        }
    };

    const handleSpeedChange = async (event) => {
        try {
            // Skip if already processing a speed change
            if (speedChangeProcessing) return;

            // Get the current playback rate
            let newSpeed;

            // Try to extract speed from event first
            if (event && typeof event === 'object') {
                newSpeed = event.playbackRate || event.speed ||
                    (event.target && event.target.speed) ||
                    (event.detail && event.detail.speed);
            }

            // If no speed in event, get directly from player
            if (!newSpeed && playerRef.current) {
                newSpeed = await playerRef.current.getPlaybackRate();
            }

            // Default to 1 if still no speed
            newSpeed = newSpeed || 1;

            // Only track if speed actually changed from the last recorded speed
            if (Math.abs(newSpeed - lastSpeedRef.current) > 0.01) {

                // Set processing flag to prevent duplicate events
                setSpeedChangeProcessing(true);

                trackPlayerAction("speed_change", {
                    speed: newSpeed,
                    previous_speed: lastSpeedRef.current
                });

                // Update the speed references
                setPlaybackSpeed(newSpeed);
                lastSpeedRef.current = newSpeed;

                // Reset the processing flag after a delay
                setTimeout(() => {
                    setSpeedChangeProcessing(false);
                }, 1000); // 1 second debounce
            }
        } catch (error) {
            console.error("Error handling speed change:", error);
            setSpeedChangeProcessing(false); // Reset flag in case of error
        }
    };

    const handleTimeUpdate = async () => {
        try {
            await handleAnalytics();

            const currentTime = Math.round(await playerRef?.current?.getCurrentTime());
            const prevTime = lastTimeRef.current;

            // If time jumped significantly, it might be a seek
            if (Math.abs(currentTime - prevTime) > 3 && prevTime > 0) {
                handleSeek({
                    currentTime: currentTime,
                    previousTime: prevTime
                });
            }

            lastTimeRef.current = currentTime;
        } catch (error) {
            console.error("Error in timeupdate handler:", error);
        }
    };

    const handleOrientationChange = () => {
        const isCurrentlyFullscreen = !!document.fullscreenElement;
        trackPlayerAction("orientation_change");
        setIsFullscreen(isCurrentlyFullscreen);
    };

    const playNextEpisode = (autoplay = false) => {
        setShowNextButton(false);
        trackPlayerAction("next_episode", {
            next_episode: autoplay ? "auto" : "manual",
        });
        if (!nextEpisodeData?.next_episode_id)
            return navigate(
                location?.pathname?.split("/").slice(0, -2).join("/")
            );
        navigate(
            location?.pathname?.split("/").slice(0, -2).join("/") +
            "/" +
            nextEpisodeData?.module_id +
            "/" +
            nextEpisodeData?.next_episode_id
        );
    };

    const [completionPercentage, setCompletionPercentage] = useState(0);

    const loaderAnimation = async () => {
        setCompletionPercentage(0);
        const interval = await setInterval(() => {
            setCompletionPercentage((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    playNextEpisode(true);
                    return 100;
                }
                return prev + 0.1;
            });
        }, 10);
        return () => clearInterval(interval);
    };

    useEffect(() => {
        document.addEventListener("fullscreenchange", handleOrientationChange);
        return () => {
            document.removeEventListener(
                "fullscreenchange",
                handleOrientationChange
            );
        };
    }, []);

    useEffect(() => {
        const pathSegments = location?.pathname?.split("/");
        setEpisodeDetail({
            module_id:
                pathSegments.length > 2
                    ? pathSegments[pathSegments.length - 2]
                    : "",
            course_id:
                pathSegments.length > 3
                    ? pathSegments[pathSegments.length - 3]
                    : "",
        });
    }, [location?.pathname]);

    useEffect(() => {
        // Initialize lastSpeedRef when component mounts
        if (playerRef.current) {
            playerRef.current.getPlaybackRate?.()
                .then(rate => {
                    lastSpeedRef.current = rate || 1;
                    setPlaybackSpeed(rate || 1);
                })
                .catch(() => {
                    lastSpeedRef.current = 1;
                    setPlaybackSpeed(1);
                });
        }
    }, []);

    return (
        <div className="w-full h-fit rounded-lg overflow-hidden relative">
            {videoData ? (
                <GumletPlayer
                    ref={playerRef}
                    videoID={videoData?.signed_url.split("/")[4]}
                    title="One Percent Club Video Player"
                    style={{
                        width: "100%",
                        position: "relative",
                        aspectRatio: "16 / 9",
                    }}
                    autoplay={false}
                    drm_token={token}
                    expires={expires}
                    t={videoData?.last_watched || 0}
                    onPause={handlePause}
                    onPlay={handlePlay}
                    onTimeUpdate={handleTimeUpdate}
                    onPlaybackRateChange={handleSpeedChange}
                    onSeeked={handleSeek}
                    onError={onError}
                    onEnded={() => {
                        handleVideoEnd();
                    }}
                    watermark_text={user?._id}
                    gm_user_id={user?._id}
                    gm_user_email={user?.email}
                    gm_user_name={user?.name}
                />
            ) : (
                <div
                    role="status"
                    className="flex items-center justify-center !w-full aspect-video bg-gray-300 rounded-lg animate-pulse dark:bg-gray-700"
                >
                    <AcademyVideoIcon />
                </div>
            )}
            {showNextButton && (
                <div className="absolute w-full h-full text-white inset-0 flex flex-col items-center justify-center bg-black bg-opacity-80 pointer-events-auto">
                    <p>Up next</p>
                    <p className="text-2xl font-bold my-2">
                        {nextEpisodeData?.title}
                    </p>
                    <div className="relative -my-4">
                        <ProgressCircularBar
                            radius={10}
                            completion_percentage={completionPercentage}
                            backRingColor="transparent"
                        />
                        <button
                            onClick={() => playNextEpisode(false)}
                            className="mt-2 p-6 w-20 h-20 text-white bg-[#262626] rounded-full absolute top-8 left-10"
                        >
                            <div className="absolute top-6 w-0 h-0 border-l-[18px] border-l-transparent border-r-[18px] border-r-transparent border-b-[32px] border-b-white-500 rotate-90"></div>
                        </button>
                    </div>
                    <p
                        className="font-semibold text-lg cursor-pointer"
                        onClick={() => {
                            setShowNextButton(false);
                        }}
                    >
                        Cancel
                    </p>
                </div>
            )}
        </div>
    );
};

export default VideoPlayer;
